import type {
  QboTransactionSearchCriteria,
  QboTransactionSearchRow,
} from '@/services/qboTransactionSearch'
import { matchTerm, filterRows, type FieldDefinition, type FilterGroup } from '@/tableControls'

export function getRowSearchableDescriptionText(row: QboTransactionSearchRow): string {
  return `${row.memo || ''} ${row.transaction_description || ''} ${row.line_item_description || ''}`
    .trim()
}

export function parseQboTxnLineAmount(amount: string | number | undefined): number | null {
  if (typeof amount === 'number') {
    return Number.isFinite(amount) ? amount : null
  }
  const raw = amount?.trim()
  if (!raw) return null
  const n = parseFloat(raw.replace(/,/g, '').replace(/\$/g, ''))
  return Number.isFinite(n) ? n : null
}

export function getQboTxnFilterValue(
  row: QboTransactionSearchRow,
  fieldKey: string,
  fields: FieldDefinition<QboTransactionSearchRow>[]
): unknown {
  const field = fields.find((entry) => entry.key === fieldKey)
  const raw = field?.accessor ? field.accessor(row) : row[fieldKey as keyof QboTransactionSearchRow]
  if (fieldKey === 'line_item_amount') {
    return parseQboTxnLineAmount(raw as string | number | undefined)
  }
  return raw
}

export function filterQboTxnRowsWithTree(
  rows: QboTransactionSearchRow[],
  filterTree: FilterGroup | null,
  fields: FieldDefinition<QboTransactionSearchRow>[]
): QboTransactionSearchRow[] {
  return filterRows(rows, filterTree, fields, (row, fieldKey) =>
    getQboTxnFilterValue(row, fieldKey, fields)
  )
}

export function getTransactionRowSearchableText(row: QboTransactionSearchRow): string {
  return [
    row.transaction_id,
    row.date,
    row.type,
    row.contact_name,
    row.memo,
    row.transaction_description,
    row.doc_number,
    row.line_item_description,
    row.line_item_category,
    row.line_item_location,
    row.line_item_amount,
    row.update_status,
  ]
    .filter(Boolean)
    .map(String)
    .join(' ')
}

export function filterTransactionRowsByText(
  rows: QboTransactionSearchRow[],
  query: string
): QboTransactionSearchRow[] {
  const trimmed = query.trim()
  if (!trimmed) return rows
  return rows.filter((row) => matchTerm(getTransactionRowSearchableText(row), trimmed))
}

export type SearchCriteriaSummaryPart = { label: string; value: string }

export function getSearchCriteriaSummaryParts(
  criteria: QboTransactionSearchCriteria
): SearchCriteriaSummaryPart[] {
  const parts: SearchCriteriaSummaryPart[] = []
  if (criteria.date_start || criteria.date_end) {
    parts.push({
      label: 'Dates',
      value: `${criteria.date_start || '…'} – ${criteria.date_end || '…'}`,
    })
  }
  const contacts = (criteria.contact ?? []).map((c) => c.trim()).filter(Boolean)
  if (contacts.length > 0) {
    parts.push({ label: 'Vendor', value: contacts.join(', ') })
  }
  if (criteria.description?.trim()) {
    parts.push({ label: 'Description', value: criteria.description.trim() })
  }
  if (criteria.doc_number?.trim()) {
    parts.push({ label: 'Ref', value: criteria.doc_number.trim() })
  }
  if (criteria.line_description?.trim()) {
    parts.push({ label: 'Line desc', value: criteria.line_description.trim() })
  }
  if (criteria.line_location?.trim()) {
    parts.push({ label: 'Location', value: criteria.line_location.trim() })
  }
  if (criteria.line_category?.trim()) {
    parts.push({ label: 'Category', value: criteria.line_category.trim() })
  }
  if (criteria.line_amount?.trim()) {
    const op = criteria.line_amount_op ?? 'eq'
    parts.push({ label: 'Amount', value: `${op} ${criteria.line_amount.trim()}` })
  }
  if (criteria.memo?.trim()) {
    parts.push({ label: 'Memo', value: criteria.memo.trim() })
  }
  if (criteria.row_change_filter && criteria.row_change_filter !== 'all') {
    parts.push({
      label: 'Changes',
      value: criteria.row_change_filter === 'with_changes' ? 'With changes' : 'Clean only',
    })
  }
  if (criteria.category_conflicts_mode) {
    parts.push({
      label: 'Similarity',
      value: String(criteria.conflict_similarity ?? 90),
    })
  }
  return parts
}

const SEARCH_CRITERIA_INLINE_MAX_CHARS = 40

const MULTI_VALUE_NOUNS: Record<string, string> = {
  Vendor: 'vendors',
  Category: 'categories',
  Location: 'locations',
}

export function formatSearchCriteriaDate(iso?: string): string {
  const raw = iso?.trim()
  if (!raw) return '…'
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  if (!match) return raw
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function splitSummaryValues(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export type SearchCriteriaRecapItem = {
  text: string
  compacted: boolean
}

export type SearchCriteriaRecap = {
  datesLine: string | null
  inlineItems: SearchCriteriaRecapItem[]
}

export function compactSearchCriteriaPart(
  part: SearchCriteriaSummaryPart
): SearchCriteriaRecapItem {
  const noun = MULTI_VALUE_NOUNS[part.label]
  if (noun) {
    const values = splitSummaryValues(part.value)
    if (values.length > 1) {
      return { text: `${values.length} ${noun}`, compacted: true }
    }
    return { text: values[0] || part.value, compacted: false }
  }
  if (part.value.length > SEARCH_CRITERIA_INLINE_MAX_CHARS) {
    return {
      text: `${part.label}: ${part.value.slice(0, SEARCH_CRITERIA_INLINE_MAX_CHARS).trimEnd()}…`,
      compacted: true,
    }
  }
  return { text: `${part.label}: ${part.value}`, compacted: false }
}

export function buildSearchCriteriaRecap(
  criteria: QboTransactionSearchCriteria
): SearchCriteriaRecap {
  const allParts = getSearchCriteriaSummaryParts(criteria)
  const otherParts = allParts.filter((part) => part.label !== 'Dates')
  const datesLine =
    criteria.date_start || criteria.date_end
      ? `${formatSearchCriteriaDate(criteria.date_start)} – ${formatSearchCriteriaDate(criteria.date_end)}`
      : null
  return {
    datesLine,
    inlineItems: otherParts.map(compactSearchCriteriaPart),
  }
}

export function formatSearchCriteriaSummary(criteria: QboTransactionSearchCriteria): string {
  const parts = getSearchCriteriaSummaryParts(criteria)
  return parts.length > 0
    ? parts.map((p) => `${p.label}: ${p.value}`).join(' · ')
    : 'No filters (all transactions in date range)'
}

export function criteriaEqual(
  a: QboTransactionSearchCriteria,
  b: QboTransactionSearchCriteria | null
): boolean {
  if (!b) return false
  return JSON.stringify(a) === JSON.stringify(b)
}
